import { type ReactNode } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { Checkbox } from "~/atoms/checkbox";
import { cn } from "~/lib/utils";

export interface AnchoringColumn<TRow> {
  key: string;
  header: ReactNode;
  cell: (row: TRow) => ReactNode;
}

interface AnchoringSelection<TId> {
  selectedIds: Set<TId>;
  allSelected: boolean;
  toggleRow: (id: TId) => void;
  toggleAll: () => void;
}

interface Props<TRow, TId> {
  columns: AnchoringColumn<TRow>[];
  rows: TRow[];
  getRowId: (row: TRow) => TId;
  emptyMessage: string;
  onRowClick?: (row: TRow) => void;
  selection?: AnchoringSelection<TId>;
  /** Rows for which this returns false render no selection checkbox. */
  isRowSelectable?: (row: TRow) => boolean;
}

/**
 * Lighter, column-driven table for the anchoring tabs — no filters sidebar,
 * column registry or virtualization, just the shared table atoms with optional
 * bulk row selection.
 */
export function AnchoringTable<TRow, TId>({
  columns,
  rows,
  getRowId,
  emptyMessage,
  onRowClick,
  selection,
  isRowSelectable,
}: Props<TRow, TId>) {
  const columnCount = columns.length + (selection ? 1 : 0);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {selection && (
            <TableHead className="w-10">
              <Checkbox
                checked={selection.allSelected}
                onCheckedChange={selection.toggleAll}
              />
            </TableHead>
          )}
          {columns.map((column) => (
            <TableHead key={column.key}>{column.header}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={columnCount}
              className="text-muted-foreground py-8 text-center"
            >
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => {
            const id = getRowId(row);
            return (
              <TableRow
                key={String(id)}
                className={cn(onRowClick && "cursor-pointer")}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {selection && (
                  <TableCell>
                    {(!isRowSelectable || isRowSelectable(row)) && (
                      <Checkbox
                        checked={selection.selectedIds.has(id)}
                        onClick={(event) => event.stopPropagation()}
                        onCheckedChange={() => selection.toggleRow(id)}
                      />
                    )}
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell key={column.key}>{column.cell(row)}</TableCell>
                ))}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
